import React from 'react';

import { useIntl } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';

import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import IconTile from '@atlaskit/icon/icon-tile';

import CrossIcon from '@atlaskit/icon/core/cross';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { getIcon } from '../utils/icon-config';

import { RecommendationButton } from './recommendation-button';

import { type RecommendationCardContext, type RecommendationCardCta } from './recommendation-card';

import { messages } from './common-messages';

import { RecommendationContainer } from './recommendation-container';

// TODO: Change optional prop to required, except the isLast prop which is for styling purposes
export interface CompactCardProps extends Omit<RecommendationCardContext, 'recommendationId'> {
	/** Handler invoked when the dismiss (cross) icon is clicked. */
	onDismiss?(): void;
	/** Returns a click handler for the given CTA. */
	handleOnCtaClick?(cta: RecommendationCardCta): () => void;
	/** Stable message instance identifier, used as a key prefix for CTA list rendering. */
	messageInstanceId?: string;
	/** When true, hides the bottom divider so the last row sits flush against the list border. */
	isLast?: boolean;
}

export const CompactCard: React.FC<CompactCardProps> = ({
	title,
	icon,
	body,
	ctas,
	avatars,
	onDismiss,
	handleOnCtaClick,
	messageInstanceId = '',
	isLast = false,
}) => {
	const { formatMessage } = useIntl();

	return (
		<RecommendationContainer variant="compact-row" isLast={isLast} testId="compact-card">
			<Inline space="space.100" spread="space-between" alignBlock="center">
				<Inline space="space.150" alignBlock="center">
					{avatars && avatars.length > 0 ? (
						<AvatarGroup
							appearance="stack"
							size="small"
							maxCount={3}
							testId="compact-card-avatars"
							data={avatars}
						/>
					) : (
						<IconTile
							icon={getIcon(icon)}
							appearance="purple"
							label=""
							size="small"
							testId="compact-card-icon"
						/>
					)}
					<Stack space="space.025">
						<Heading as="h3" size="xsmall">
							{title}
						</Heading>
						<Text color="color.text.subtle">{body}</Text>
					</Stack>
				</Inline>
				<Inline space="space.100" alignBlock="center">
					<IconButton
						icon={CrossIcon}
						appearance="subtle"
						label={formatMessage(messages.crossIconLabel)}
						onClick={onDismiss}
					/>
					{[...ctas]
						.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
						.map((cta) => (
							<RecommendationButton
								buttonType={cta.actionType ?? null}
								label={cta.label}
								onClick={handleOnCtaClick?.(cta)}
								key={`${messageInstanceId}-${cta.label}`}
							/>
						))}
				</Inline>
			</Inline>
		</RecommendationContainer>
	);
};
